import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Brain,
  RefreshCw,
  Check,
  Clock,
  X as XIcon,
  Loader2,
  Phone,
  MessageSquare,
  Mail,
  Users,
  ChevronRight,
  Sparkles,
  TrendingUp,
} from "lucide-react";

const PRIORITY_COLORS = {
  ALTA: { bg: "rgba(220,38,38,0.08)", border: "#DC2626", text: "#991B1B", chip: "rgba(220,38,38,0.15)" },
  MEDIA: { bg: "rgba(183,149,91,0.08)", border: "#B7955B", text: "#8a6e3d", chip: "rgba(183,149,91,0.15)" },
  BASSA: { bg: "rgba(71,85,105,0.06)", border: "#94A3B8", text: "#475569", chip: "rgba(148,163,184,0.15)" },
};

const TYPE_ICONS = {
  lead_cooling: Phone,
  stuck_practice: Clock,
  dpr_warning: TrendingUp,
  campaign_idea: Sparkles,
  daily_recap: Brain,
  weekly_recap: Brain,
  appointment_prep: Users,
  general: Brain,
};

const CHANNEL_ICONS = {
  chiama: Phone,
  whatsapp: MessageSquare,
  email: Mail,
  incontro: Users,
  nessuno: null,
};

function InsightCard({ insight, onDone, onSnooze, onDismiss }) {
  const colors = PRIORITY_COLORS[insight.priority] || PRIORITY_COLORS.MEDIA;
  const Icon = TYPE_ICONS[insight.type] || Brain;
  const ChannelIcon = CHANNEL_ICONS[insight.channel] || null;
  const [actioning, setActioning] = useState(null);

  const wrap = (fn, label) => async () => {
    setActioning(label);
    try {
      await fn(insight.id);
    } finally {
      setActioning(null);
    }
  };

  return (
    <div
      className="relative bg-white"
      style={{
        border: `1px solid ${colors.border}40`,
        borderLeft: `4px solid ${colors.border}`,
        borderRadius: 8,
        padding: 14,
      }}
      data-testid={`brain-insight-${insight.id}`}
    >
      <div className="flex items-start gap-3">
        <div
          className="shrink-0 flex items-center justify-center"
          style={{
            width: 36, height: 36, borderRadius: 8,
            background: colors.bg, color: colors.border,
          }}
        >
          <Icon className="w-4 h-4" strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span
              className="text-[9px] font-bold uppercase tracking-[0.16em] px-1.5 py-0.5 rounded"
              style={{ background: colors.chip, color: colors.text }}
            >
              {insight.priority}
            </span>
            <span className="text-[9px] uppercase tracking-wider text-slate-400 font-medium">
              {insight.type.replace(/_/g, " ")}
            </span>
          </div>
          <h4 className="text-[14px] font-semibold text-slate-900 leading-snug">{insight.title}</h4>
          <p className="text-[12.5px] text-slate-600 mt-1 leading-relaxed">{insight.body}</p>
          {insight.suggestedAction && (
            <div className="mt-2 flex items-center gap-1.5 flex-wrap">
              {ChannelIcon && <ChannelIcon className="w-3 h-3" style={{ color: colors.border }} />}
              <span className="text-[11.5px] font-medium" style={{ color: colors.text }}>
                {insight.suggestedAction}
              </span>
              {insight.clientId && (
                <Link
                  to={`/clienti/${insight.clientId}`}
                  className="ml-1 inline-flex items-center gap-0.5 text-[11px] underline-offset-2 hover:underline"
                  style={{ color: colors.border }}
                  data-testid={`brain-goclient-${insight.id}`}
                >
                  Apri cliente <ChevronRight className="w-3 h-3" />
                </Link>
              )}
            </div>
          )}
          {/* Azioni */}
          <div className="flex items-center gap-1.5 mt-3">
            <button
              type="button"
              onClick={wrap(onDone, "done")}
              disabled={!!actioning}
              data-testid={`brain-done-${insight.id}`}
              className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 transition"
              title="Marca come fatto"
            >
              {actioning === "done" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Fatto
            </button>
            <button
              type="button"
              onClick={wrap(onSnooze, "snooze")}
              disabled={!!actioning}
              data-testid={`brain-snooze-${insight.id}`}
              className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition"
              title="Rimanda di 4h"
            >
              {actioning === "snooze" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Clock className="w-3 h-3" />} Dopo
            </button>
            <button
              type="button"
              onClick={wrap(onDismiss, "dismiss")}
              disabled={!!actioning}
              data-testid={`brain-dismiss-${insight.id}`}
              className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded border border-slate-200 text-slate-400 hover:bg-slate-50 hover:text-slate-600 disabled:opacity-50 transition"
              title="Ignora questo insight"
            >
              <XIcon className="w-3 h-3" /> Ignora
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BrainPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dna, setDna] = useState(null);

  const reload = useCallback(async () => {
    try {
      const [insR, dnaR] = await Promise.all([
        api.get("/brain/insights"),
        api.get("/brain/dna").catch(() => null),
      ]);
      setItems(insR.data?.items || []);
      if (dnaR) setDna(dnaR.data || null);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/brain/refresh");
      toast.success(`Comandante aggiornato — ${r.data?.new_insights || 0} nuovi insight`);
      await reload();
    } catch (e) {
      toast.error("Impossibile aggiornare ora — riprova fra qualche minuto");
    } finally {
      setRefreshing(false);
    }
  };

  const handleDone = async (id) => {
    try { await api.post(`/brain/insights/${id}/done`); setItems((arr) => arr.filter((x) => x.id !== id)); toast.success("Marcato come fatto"); } catch {}
  };
  const handleSnooze = async (id) => {
    try { await api.post(`/brain/insights/${id}/snooze`); setItems((arr) => arr.filter((x) => x.id !== id)); toast.info("Rimandato di 4 ore"); } catch {}
  };
  const handleDismiss = async (id) => {
    try { await api.post(`/brain/insights/${id}/dismiss`); setItems((arr) => arr.filter((x) => x.id !== id)); toast.info("Ignorato"); } catch {}
  };

  return (
    <div
      className="bg-white"
      style={{
        border: "1px solid #E8ECF2",
        borderRadius: 12,
        boxShadow: "0 1px 3px rgba(10,25,47,0.04)",
        overflow: "hidden",
      }}
      data-testid="brain-panel"
    >
      {/* Header */}
      <div
        className="flex items-center justify-between gap-2 px-4 py-3"
        style={{
          background: "linear-gradient(135deg, #0A192F 0%, #0D1E35 50%, #B7955B 200%)",
          color: "#fff",
        }}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <div
            className="flex items-center justify-center shrink-0"
            style={{
              width: 32, height: 32, borderRadius: 8,
              background: "rgba(183,149,91,0.2)",
              border: "1px solid rgba(183,149,91,0.4)",
            }}
          >
            <Brain className="w-4 h-4" style={{ color: "#D4AB6E" }} strokeWidth={2.2} />
          </div>
          <div className="min-w-0">
            <h3 className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: "#B7955B" }}>
              IL COMANDANTE ti dice
            </h3>
            <p className="text-[15px] font-serif leading-tight truncate">
              {items.length === 0 ? "Nessuna iniziativa al momento" : `${items.length} ${items.length === 1 ? "iniziativa" : "iniziative"} proattive`}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="brain-refresh"
          className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded transition disabled:opacity-50"
          style={{
            background: "rgba(183,149,91,0.18)",
            border: "1px solid rgba(183,149,91,0.4)",
            color: "#D4AB6E",
          }}
          title="Genera nuovi insight adesso (solo su richiesta — nessun costo automatico)"
        >
          {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          {refreshing ? "Genero..." : "Genera insight"}
        </button>
      </div>

      {/* Mini DNA hints */}
      {dna && (
        <div className="px-4 py-2 text-[10.5px] text-slate-500 bg-slate-50/60 border-b border-slate-100 flex items-center gap-3 flex-wrap">
          <span><b className="text-slate-700">{dna.clients_total}</b> clienti</span>
          {dna.note_style && (
            <span>· Stile: <b className="text-slate-700">{dna.note_style}</b></span>
          )}
          {dna.best_cat && (
            <span>· Tu spacchi con: <b className="text-[#B7955B]">{dna.best_cat}</b></span>
          )}
          {Array.isArray(dna.top_active_hours) && dna.top_active_hours.length > 0 && (
            <span>· Ore top: <b className="text-slate-700">{dna.top_active_hours.map((h) => `${h}h`).join(" · ")}</b></span>
          )}
        </div>
      )}

      {/* Body */}
      <div className="p-3 space-y-2">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-[#B7955B]" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 px-4 text-slate-400">
            <Brain className="w-8 h-8 mx-auto mb-2 opacity-30" strokeWidth={1.5} />
            <p className="text-[13px] font-medium text-slate-500">Il Comandante non ha iniziative pendenti</p>
            <p className="text-[11px] mt-1">Gli insight si generano solo su tua richiesta: premi il bottone quando vuoi un'analisi fresca dei tuoi clienti.</p>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="brain-refresh-empty"
              className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 text-[12px] font-semibold rounded-lg transition disabled:opacity-50"
              style={{ background: "rgba(183,149,91,0.12)", border: "1px solid rgba(183,149,91,0.4)", color: "#8a6e3d" }}
            >
              {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              {refreshing ? "Sto analizzando..." : "Genera insight ora"}
            </button>
          </div>
        ) : (
          items.map((ins) => (
            <InsightCard
              key={ins.id}
              insight={ins}
              onDone={handleDone}
              onSnooze={handleSnooze}
              onDismiss={handleDismiss}
            />
          ))
        )}
      </div>
    </div>
  );
}
